import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import Layout from './Layout';
import { lireProfil, majProfil } from './api';

const dateCourte = (v) => (v ? new Date(v).toLocaleDateString('fr-BE') : '');

const COULEUR_NIVEAU = {
  PROPRIETAIRE: 'success',
  GESTION: 'primary',
  LECTURE: 'default',
};

function Ligne({ gauche, droite }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      <Typography variant="body2" color="text.secondary">{gauche}</Typography>
      <Typography variant="body2" sx={{ textAlign: 'right' }}>{droite}</Typography>
    </Stack>
  );
}

/**
 * Le profil.
 *
 * Les droits ne sont pas une propriété de la personne mais de sa relation à
 * chaque dossier : propriétaire du sien, invité en gestion ou en lecture sur
 * celui d'un autre. Les montrer dossier par dossier évite de laisser croire à
 * un droit global qui n'existe pas.
 */
const CIVILITES = ['Monsieur', 'Madame', 'Autre'];
const PAYS = ['Luxembourg', 'Belgique', 'France', 'Allemagne', 'Pays-Bas'];

/** Ce que le titulaire corrige lui-même. Son rôle ne s'y trouve pas. */
const MODIFIABLES = ['first_name', 'last_name', 'genre', 'phone',
                     'street', 'number', 'letter_box', 'postcode', 'city',
                     'country'];

export default function Profil() {
  const naviguer = useNavigate();
  const [profil, setProfil] = useState(null);
  const [erreur, setErreur] = useState('');
  const [edition, setEdition] = useState(null);
  const [message, setMessage] = useState('');
  const [occupe, setOccupe] = useState(false);

  useEffect(() => {
    let vivant = true;
    lireProfil()
      .then((d) => vivant && setProfil(d))
      .catch(() => vivant && setErreur('Impossible de charger votre profil.'));
    return () => { vivant = false; };
  }, []);

  if (erreur) {
    return <Layout titre="Votre profil"><Alert severity="error">{erreur}</Alert></Layout>;
  }
  if (!profil) {
    return <Layout titre="Votre profil"><CircularProgress size={28} /></Layout>;
  }

  const { utilisateur: u, organisation: o, resume: r } = profil;

  const ouvrirEdition = () => {
    setMessage('');
    setEdition(Object.fromEntries(
      MODIFIABLES.map((nom) => [nom, u[nom] ?? ''])));
  };

  const enregistrer = async () => {
    setOccupe(true);
    setErreur('');
    try {
      setProfil(await majProfil(edition));
      setEdition(null);
      setMessage('Vos coordonnées sont enregistrées.');
    } catch {
      setErreur("L'enregistrement n'a pas abouti. Réessayez.");
    } finally {
      setOccupe(false);
    }
  };

  const champ = (nom, label, props = {}) => (
    <TextField
      label={label} value={edition?.[nom] ?? ''} fullWidth
      onChange={(e) => setEdition((c) => ({ ...c, [nom]: e.target.value }))}
      {...props}
    />
  );

  return (
    <Layout
      titre="Votre profil"
      introduction="Qui vous êtes, pour qui vous travaillez, et ce à quoi vous avez accès."
      actions={
        <Button variant="outlined" onClick={() => naviguer('/logout')}>
          Se déconnecter
        </Button>
      }
    >
      <Stack spacing={3}>
        {message && <Alert severity="success">{message}</Alert>}

        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack direction="row" justifyContent="space-between"
                 alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h3">Vous</Typography>
            {!edition && (
              <Button size="small" variant="outlined" onClick={ouvrirEdition}>
                Compléter
              </Button>
            )}
          </Stack>

          {edition ? (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Ces coordonnées seront reprises comme les vôtres sur chaque
                dossier que vous ouvrirez : c'est ce nom et cette adresse qui
                figureront sur vos déclarations.
              </Typography>

              <TextField select label="Civilité" value={edition.genre ?? ''}
                         onChange={(e) => setEdition((c) => ({
                           ...c, genre: e.target.value }))}
                         sx={{ maxWidth: 220 }}>
                <MenuItem value="">Non précisée</MenuItem>
                {CIVILITES.map((g) => (
                  <MenuItem key={g} value={g}>{g}</MenuItem>
                ))}
              </TextField>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                {champ('first_name', 'Prénom')}
                {champ('last_name', 'Nom')}
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                {/* L'adresse est celle du compte : les invitations y sont
                    envoyées, elle ne se change pas d'un clic. */}
                <TextField label="Adresse électronique" fullWidth disabled
                           value={profil.utilisateur.adresse_verifiee || ''}
                           helperText="C'est l'adresse de votre compte, vérifiée à l'inscription. Pour en changer, écrivez au support." />
                {champ('phone', 'Téléphone ou GSM')}
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                {champ('street', 'Rue', { sx: { flex: 3 } })}
                {champ('number', 'Numéro', { sx: { flex: 1 } })}
                {champ('letter_box', 'Boîte', { sx: { flex: 1 } })}
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                {champ('postcode', 'Code postal', { sx: { flex: 1 } })}
                {champ('city', 'Localité', { sx: { flex: 2 } })}
                <TextField select label="Pays" sx={{ flex: 2 }}
                           value={edition.country ?? ''}
                           onChange={(e) => setEdition((c) => ({
                             ...c, country: e.target.value }))}>
                  <MenuItem value="">Non précisé</MenuItem>
                  {PAYS.map((p) => (
                    <MenuItem key={p} value={p}>{p}</MenuItem>
                  ))}
                </TextField>
              </Stack>

              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button onClick={() => setEdition(null)}>Annuler</Button>
                <Button variant="contained" onClick={enregistrer}
                        disabled={occupe}>
                  Enregistrer
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Stack spacing={0.75}>
              {u.nom && (
                <Ligne gauche="Nom"
                       droite={`${u.genre ? `${u.genre} ` : ''}${u.nom}`} />
              )}
              <Ligne gauche="Identifiant" droite={u.identifiant} />
              <Ligne gauche="Adresse électronique" droite={u.adresse_verifiee || '—'} />
              <Ligne gauche="Téléphone" droite={u.phone || '—'} />
              <Ligne gauche="Adresse" droite={u.adresse_complete || '—'} />
              <Ligne gauche="Qualité" droite={u.role_libelle} />
              <Ligne gauche="Compte ouvert le" droite={dateCourte(u.depuis)} />
            </Stack>
          )}
          {!edition && !u.nom && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Votre nom n'est pas renseigné. Il figurera tel quel — c'est-à-dire
              vide — sur vos déclarations de sinistre.
            </Alert>
          )}

          {u.equipe_interne && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Vous faites partie de l'équipe : vous pouvez corriger le
              référentiel — compagnies, conditions générales, intermédiaires —
              que les autres comptes ne font que consulter.
            </Alert>
          )}
        </Paper>

        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h3" sx={{ mb: 2 }}>Votre organisation</Typography>
          {o ? (
            <Stack spacing={0.75}>
              <Ligne gauche="Nom" droite={o.nom} />
              <Ligne gauche="Type" droite={o.role_libelle || o.role} />
            </Stack>
          ) : (
            <Alert severity="warning">
              Aucune organisation n'est rattachée à votre compte. Vous ne
              pourrez ni ouvrir de dossier, ni en recevoir : signalez-le nous.
            </Alert>
          )}
        </Paper>

        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h3" sx={{ mb: 1 }}>Vos accès</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Un droit s'attache à un dossier, pas à un compte : vous êtes
            propriétaire des vôtres, et invité sur ceux qu'on vous partage.
          </Typography>

          <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap"
                 useFlexGap>
            <Chip size="small" variant="outlined"
                  label={`${r.dossiers} dossier${r.dossiers > 1 ? 's' : ''}`} />
            {r.proprietaire > 0 && (
              <Chip size="small" color="success" variant="outlined"
                    label={`${r.proprietaire} à vous`} />
            )}
            {r.partages > 0 && (
              <Chip size="small" color="primary" variant="outlined"
                    label={`${r.partages} partagé${r.partages > 1 ? 's' : ''}`} />
            )}
          </Stack>

          <Divider sx={{ mb: 2 }} />

          <Stack spacing={1}>
            {profil.acces.map((a) => (
              <Stack key={a.id} direction={{ xs: 'column', sm: 'row' }}
                     spacing={1.5} alignItems={{ sm: 'center' }}
                     justifyContent="space-between">
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {a.nom}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {a.reference ? `${a.reference} · ` : ''}
                    modifié le {dateCourte(a.modifie_le)}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip size="small" variant="outlined"
                        color={COULEUR_NIVEAU[a.niveau] || 'default'}
                        label={a.niveau_libelle} />
                  <Button size="small"
                          onClick={() => naviguer(`/mon-dossier/${a.id}/sinistre`)}>
                    Ouvrir
                  </Button>
                </Stack>
              </Stack>
            ))}
            {profil.acces.length === 0 && (
              <Alert severity="info">
                Vous n'avez encore accès à aucun dossier.
              </Alert>
            )}
          </Stack>
        </Paper>
      </Stack>
    </Layout>
  );
}
